import { UserSummary, ProfileMin } from '../../../../../common/types/user';

export enum FileStatus {
  Active = 'active',
  Archived = 'archived',
  Removed = 'removed',
  error = 'error',
}

export enum FileDocumentType {
  File = 'file',
  Link = 'link',
}

export enum RelatedEntityType {
  User = 'user',
  Aircraft = 'aircraft',
  AircraftLog = 'aircraft_log',
  CompanyDocument = 'company_document',
  Contract = 'contract',
  Client = 'client',
  TrainingItem = 'training_item',
  Schedule = 'schedule',
}

export interface RelatedEntity {
  Type: RelatedEntityType;
  ID: string | null;
}

export enum AcknowledgementPolicy {
  BeforeFlight = 'before_flight',
  Anytime = 'anytime',
}

export enum ReminderFrequency {
  None = 'none',
  Daily = 'daily',
  Every3Days = 'every_3_days',
  Weekly = 'weekly',
  Biweekly = 'biweekly',
}

export const ReminderFrequencyLabels: Record<ReminderFrequency, string> = {
  [ReminderFrequency.None]: 'None',
  [ReminderFrequency.Daily]: 'Daily',
  [ReminderFrequency.Every3Days]: 'Every 3 days',
  [ReminderFrequency.Weekly]: 'Weekly',
  [ReminderFrequency.Biweekly]: 'Every 2 weeks',
};

/**
 * Selects company users by id, role or tag. An empty acknowledgement audience
 * means "everyone who can view the document". The alert audience is always the
 * acknowledgers plus whoever it selects, so empty means "acknowledgers only".
 */
export interface MemoAudience {
  Users?: string[];
  Roles?: string[];
  Tags?: string[];
}

export const emptyAudience = (): MemoAudience => ({ Users: [], Roles: [], Tags: [] });

export const isAudienceEmpty = (a?: MemoAudience | null): boolean =>
  !a ||
  ((a.Users?.length || 0) === 0 && (a.Roles?.length || 0) === 0 && (a.Tags?.length || 0) === 0);

/** Memo configuration as edited in the document modal and sent with uploads. */
export interface MemoConfig {
  IsMemo: boolean;
  AcknowledgementPolicy: AcknowledgementPolicy;
  AcknowledgementAudience: MemoAudience;
  AlertAudience: MemoAudience;
  AcknowledgementText: string;
  ReminderFrequency: ReminderFrequency;
}

export const defaultMemoConfig = (): MemoConfig => ({
  IsMemo: false,
  AcknowledgementPolicy: AcknowledgementPolicy.Anytime,
  AcknowledgementAudience: emptyAudience(),
  AlertAudience: emptyAudience(),
  AcknowledgementText: '',
  ReminderFrequency: ReminderFrequency.None,
});

export const memoConfigFromFile = (file?: Partial<FileStruct> | null): MemoConfig => ({
  IsMemo: !!file?.IsMemo,
  AcknowledgementPolicy: file?.AcknowledgementPolicy || AcknowledgementPolicy.Anytime,
  AcknowledgementAudience: { ...emptyAudience(), ...(file?.AcknowledgementAudience || {}) },
  AlertAudience: { ...emptyAudience(), ...(file?.AlertAudience || {}) },
  AcknowledgementText: file?.AcknowledgementText || '',
  ReminderFrequency: file?.ReminderFrequency || ReminderFrequency.None,
});

export interface GenerationInfo {
  ModifiedDate: Date;
  Generation: number;
  ModifiedBy: string;
  ProfileMin: ProfileMin;
}

export interface FileStruct {
  ID: string;
  CompanyID: string;
  CreatedAt: string;
  UpdatedAt?: string;
  UpdatedBy?: string;

  Notes?: string;
  Status: FileStatus;

  Type?: FileDocumentType;

  CurrentGeneration: number;
  Generations: number[];
  GenerationsInfo: GenerationInfo[];
  MimeType: string;
  Size: number;
  FileClientName: string;
  FileName: string;
  Ext: string;
  Url: string;
  ExpiresAt: number; // unix timestamp
  RelatedEntities: RelatedEntity[];
  UpdatedByUser: UserSummary;
  ExpirationDate: string;
  EffectiveDate: string;
  FolderID: string;
  UserPermissions?: LibraryEntityUserPermission;
  RolePermissions?: LibraryEntityRolePermission;
  TagPermissions?: LibraryEntityTagPermission;

  // Memo fields (see MemoConfig)
  IsMemo?: boolean;
  AcknowledgementPolicy?: AcknowledgementPolicy;
  AcknowledgementAudience?: MemoAudience;
  AlertAudience?: MemoAudience;
  AcknowledgementText?: string | null;
  ReminderFrequency?: ReminderFrequency;
  MemoPublishedAt?: string | null;
  RestrictedUsers?: LibraryEntityRestrictedUserPermission;
}

export interface Folder {
  ID: string;
  Name: string;
  CurrentFolderID: string;
  CompanyID?: string;
  CreatedAt?: string;
  UpdatedAt?: string;
  UpdatedBy?: string;
  UserPermissions?: LibraryEntityUserPermission;
  RolePermissions?: LibraryEntityRolePermission;
  TagPermissions?: LibraryEntityTagPermission;
  RestrictedUsers?: LibraryEntityRestrictedUserPermission;
}

interface LibraryEntityUserPermission {
  AllowedUsers?: string[];
}

interface LibraryEntityRolePermission {
  AllowedRoles?: string[];
}

interface LibraryEntityTagPermission {
  AllowedTags?: string[];
}

interface LibraryEntityRestrictedUserPermission {
  AllowedUsers?: string[];
}

export interface PermissionLists {
  Users: string[];
  Roles: string[];
  Tags: string[];
}

export interface EntityPermissions {
  Explicit: PermissionLists;
  Inherits: boolean;
  Inherited: PermissionLists;
  InheritedFromFolderID: string | null;
  InheritedFromFolderName: string;
  Effective: PermissionLists;
}

export const isLinkDocument = (file?: Pick<FileStruct, 'Type'> | null): boolean =>
  file?.Type === FileDocumentType.Link;

/**
 * Whether Company Library memos are on for a company: only when the Memos
 * setting is explicitly true. Unset (null) means off, so the feature has to
 * be switched on per company.
 */
export const memosEnabled = (settings?: { Memos?: boolean | null } | null): boolean =>
  settings?.Memos === true;

/** Roles that can configure memos, see the full roster, send reminders and download reports. */
export const MEMO_MANAGER_ROLES = ['admin', 'owner', 'companyLibraryEditor'] as const;

/** Can a user with these roles manage memos? See MEMO_MANAGER_ROLES. */
export const canManageMemos = (userRoles?: readonly string[] | null): boolean =>
  !!userRoles && MEMO_MANAGER_ROLES.some(role => userRoles.includes(role));
